import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm V GPT, Varan's AI assistant. Ask me anything about his work, skills, or experience!",
      timestamp: new Date().toISOString()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const sendMessage = useCallback(async (userMessage) => {
    if (!userMessage?.trim() || isLoading) {
      return;
    }

    // Add user message to chat
    const userMsg = {
      role: 'user',
      content: userMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);

    // Create placeholder for AI message that will be updated during simulated streaming
    const aiMessageId = Date.now();
    const aiMsg = {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };

    setMessages(prev => [...prev, aiMsg]);

    try {
      // Prepare conversation history (last 10 messages, excluding the one we just added)
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Call Netlify function
      const response = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.trim(),
          conversationHistory
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      // Simulate streaming by displaying text character by character
      const fullMessage = data.message;
      let currentIndex = 0;

      // Function to add characters gradually
      const streamText = () => {
        if (currentIndex < fullMessage.length) {
          // Add 2-5 characters at a time for smooth but fast streaming
          const chunkSize = Math.floor(Math.random() * 4) + 2;
          const chunk = fullMessage.slice(currentIndex, currentIndex + chunkSize);
          currentIndex += chunkSize;

          setMessages(prev => prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content: fullMessage.slice(0, currentIndex) }
              : msg
          ));

          // Continue streaming with a small delay
          setTimeout(streamText, 20); // 20ms between chunks for smooth animation
        } else {
          // Streaming complete, mark as finished
          setMessages(prev => prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content: fullMessage, timestamp: data.timestamp, isStreaming: false }
              : msg
          ));
          setIsLoading(false);
        }
      };

      // Start the streaming simulation
      streamText();
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message);

      // Remove the placeholder AI message and add error message
      setMessages(prev => prev.filter(msg => msg.id !== aiMessageId));

      // Add error message to chat
      const errorMsg = {
        role: 'assistant',
        content: err.message.includes('rate') || err.message.includes('limit')
          ? err.message
          : "Oops! I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMsg]);
      setIsLoading(false);
    }
  }, [messages, isLoading]);

  const clearMessages = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: "Hi! I'm V GPT, Varan's AI assistant. Ask me anything about his work, skills, or experience!",
        timestamp: new Date().toISOString()
      }
    ]);
    setError(null);
  }, []);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const toggleMaximize = useCallback(() => {
    setIsMaximized(prev => !prev);
  }, []);

  const value = {
    messages,
    isLoading,
    error,
    isOpen,
    isMaximized,
    sendMessage,
    clearMessages,
    toggleChat,
    openChat: () => setIsOpen(true),
    closeChat: () => setIsOpen(false),
    toggleMaximize
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
